import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { CreatePlaceCommand } from '../commands/hub/place/create.place.command';
import { HubVisitorRegion } from '../commands/visitor/all.frontend.settings.command';
import { CreateTripTempCommand, CreateWholeTripCommand } from '../commands/visitor/create.trip.command';
import { VisitorCreateEventCommand } from '../commands/visitor/event/create.event.command';
import { isNullOrUndefined, isObject } from '../common/extensions';
import { ApiService } from '../common/services/api.service';
import { EventAnalytics } from '../components/visitor/event.details.modal/event.details.modal.component';
import { Analytics, PlaceAnalytics } from '../components/visitor/place.details.modal/place.details.modal.component';
import { HubEvent } from '../models/hub/hub.event';
import { HubPlace } from '../models/hub/hub.place';
import { HubTour } from '../models/hub/hub.tour';
import { EventTimeService } from './event.time.service';
import { GooglePlaceReview, HubPlaceAdditionalData } from '../models/hub/hub.google.place.data';
import { HubAnalytics } from 'components/visitor/trip/trip.side.bar/visitor.trip.side.bar.component';
import { ConfigService } from './config.service';
import { HttpClient } from '@angular/common/http';
import { catchError, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

@Injectable()
export class VisitorApiService {
  constructor(private apiService: ApiService, private http: HttpClient) {}

  public getThemeSetting(hubName: string) {
    return this.apiService.get(`hubs/${hubName}/theme`);
  }

  public getPlaceCategoryToggle(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/place-category-toggle`);
  }

  public getCustomDefaultPlaceCategories(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/custom-default-place-categories`);
  }

  public getPlaceCategories() {
    return this.apiService.get(`common/place-categories`);
  }

  public searchCities(hubName, search) {
    return this.apiService.get(`visitors/${hubName}/trips/search-cities?searchText=${search}`);
  }

  public getEvents(hubName, paramObj): Observable<HubEvent[]> {
    return this.apiService.get(`visitors/${hubName}/events`, paramObj).map((data) => {
      data.forEach((item) => {
        EventTimeService.processEventTime(item);
        // to fix the Facebook event ticket URL contains 'lwww' issue, which was caused by the replacement of 'm.facebook' to 'www.facebook', but the initial URL is 'lm.facebook',
        // then remove the prefix and suffix to avoid jump to facebook firstly
        if (item.ticketUri && item.ticketUri.startsWith('http')) {
          let url = item.ticketUri.replace('lwww', 'lm');
          url = url.replace('https://lm.facebook.com/l.php?u=', '');
          url = decodeURIComponent(url);
          url = url.split('fbclid')[0];
          url = url.endsWith('?') ? url.substr(0, url.length - 1) : url;
          item.ticketUri = url;
        }
        //DefaultImageWebSideService.getDefaultImageByKeyword(item);
      });
      return data;
    });
  }

  public getPlaces(hubName, paramObj): Observable<HubPlace[]> {
    return this.apiService.get(`visitors/${hubName}/places`, paramObj).map((data) => {
      this.processPlaceData(data);
      return data;
    });
  }

  public getNearByPlaces(targetPlace: HubPlace, source: string): Observable<HubPlace[]> {
    const params = {
      page: 1,
      limit: 50,
      category: 'All',
      source: source,
    };

    return this.http
      .post(`${ConfigService.config.apiAddress}visitors/${targetPlace.hubName}/places/nearby-places`, {
        targetPlace,
        range: 4000,
      })
      .pipe(
        mergeMap((data) => {
          this.processPlaceData(data);
          return of(data as HubPlace[]);
        }),
        catchError((error) => this.getPlaces(targetPlace.hubName, params))
      );
  }

  private processPlaceData(data) {
    data.forEach((item) => {
      if (!isNullOrUndefined(item.lat) && !isNullOrUndefined(item.lng)) {
        let address = item.address;
        item.address = {
          address,
          lat: item.lat,
          lng: item.lng,
        };
      }
      if (item.hours) {
        item.hoursText = ['Monday: ', 'Tuesday: ', 'Wednesday: ', 'Thursday: ', 'Friday: ', 'Saturday: ', 'Sunday: '];
        if (isObject(item.hours)) {
          this.setPlaceHourForObject(item);
        } else if (Array.isArray(item.hours)) {
          item.hours.forEach((x) => {
            this.setPlaceHourText(x.key, item, x);
          });
        }
      }
    });
  }

  private setPlaceHourForObject(item) {
    item.hours.mon_1_open && (item.hoursText[0] += item.hours.mon_1_open);
    item.hours.mon_1_close && (item.hoursText[0] += ` - ${item.hours.mon_1_close}`);
    item.hours.tue_1_open && (item.hoursText[1] += item.hours.tue_1_open);
    item.hours.tue_1_close && (item.hoursText[1] += ` - ${item.hours.tue_1_close}`);
    item.hours.wed_1_open && (item.hoursText[2] += item.hours.wed_1_open);
    item.hours.wed_1_close && (item.hoursText[2] += ` - ${item.hours.wed_1_close}`);
    item.hours.thu_1_open && (item.hoursText[3] += item.hours.thu_1_open);
    item.hours.thu_1_close && (item.hoursText[3] += ` - ${item.hours.thu_1_close}`);
    item.hours.fri_1_open && (item.hoursText[4] += item.hours.fri_1_open);
    item.hours.fri_1_close && (item.hoursText[4] += ` - ${item.hours.fri_1_close}`);
    item.hours.sat_1_open && (item.hoursText[5] += item.hours.sat_1_open);
    item.hours.sat_1_close && (item.hoursText[5] += ` - ${item.hours.sat_1_close}`);
    item.hours.sun_1_open && (item.hoursText[6] += item.hours.sun_1_open);
    item.hours.sun_1_close && (item.hoursText[6] += ` - ${item.hours.sun_1_close}`);
  }

  private setPlaceHourText(key, item, value) {
    switch (key) {
      case 'mon_1_open':
        item.hoursText[0] += value;
        break;
      case 'mon_1_close':
        item.hoursText[0] += ` - ${value}`;
        break;
      case 'tue_1_open':
        item.hoursText[1] += value;
        break;
      case 'tue_1_close':
        item.hoursText[1] += ` - ${value}`;
        break;
      case 'wed_1_open':
        item.hoursText[2] += value;
        break;
      case 'wed_1_close':
        item.hoursText[2] += ` - ${value}`;
        break;
      case 'thu_1_open':
        item.hoursText[3] += value;
        break;
      case 'thu_1_close':
        item.hoursText[3] += ` - ${value}`;
        break;
      case 'fri_1_open':
        item.hoursText[4] += value;
        break;
      case 'fri_1_close':
        item.hoursText[4] += ` - ${value}`;
        break;
      case 'sat_1_open':
        item.hoursText[5] += value;
        break;
      case 'sat_1_close':
        item.hoursText[5] += ` - ${value}`;
        break;
      case 'sun_1_open':
        item.hoursText[6] += value;
        break;
      case 'sun_1_close':
        item.hoursText[6] += ` - ${value}`;
        break;
    }
  }

  public getGooglePlaceDetails(hubName, placeId, waitForUploading = false): Observable<HubPlaceAdditionalData> {
    return this.apiService.get(
      `visitors/${hubName}/places/google-place-details?placeId=${placeId}&waitForUploading=${waitForUploading}`
    );
  }

  public getGooglePlaceReviews(placeId: string, sort: string): Observable<GooglePlaceReview[]> {
    return this.apiService.get(`common/google-reviews?placeId=${placeId}&sort=${sort}`);
  }

  public getMatchedFBPlace(hubName, placeName, lat, lng): Observable<any> {
    return this.apiService.get(
      `visitors/${hubName}/places/matched-fb-place?placeName=${placeName}&lat=${lat}&lng=${lng}`
    );
  }

  public getMatchedFBPlaceId(hubName, placeName, lat, lng): Observable<any> {
    placeName = encodeURIComponent(placeName);
    return this.apiService.get(
      `visitors/${hubName}/places/matched-fb-place-id?placeName=${placeName}&lat=${lat}&lng=${lng}`
    );
  }

  public addShareCount(hubName, tourId) {
    return this.apiService.put(`visitors/${hubName}/tours/add-share-count`, { tourId });
  }

  public addDownloadCount(hubName, tourId) {
    return this.apiService.put(`visitors/${hubName}/tours/add-download-count`, { tourId });
  }

  public getTours(hubName): Observable<HubTour[]> {
    return this.apiService.get(`visitors/${hubName}/tours`).map((data) => {
      if (data && data.trips && data.trips.length) {
        data.trips.forEach((trip) => {
          let array = [];
          trip.attractions &&
            trip.attractions.forEach((x) => {
              if (x.status.toLowerCase() === 'ok') {
                if (x.item.startTime) {
                  x.item = EventTimeService.processEventTime(x.item);
                }
                array.push(x);
              }
            });
          trip.attractions = array;
        });
      }
      return data;
    });
  }

  public getRegion(hubName, regionId): Observable<HubVisitorRegion> {
    return this.apiService.get(`visitors/${hubName}/trips/region?regionId=${regionId}`);
  }

  public getRegionByCity(hubName, cityId): Observable<HubVisitorRegion> {
    return this.apiService.get(`visitors/${hubName}/trips/city-region?cityId=${cityId}`);
  }

  public getUserTrips(hubName, userFacebookId) {
    return this.apiService.get(`visitors/${hubName}/trips/user/${userFacebookId}`).map((data) => {
      if (data && data.trips && data.trips.length) {
        data.trips.forEach((trip) => {
          let array = [];
          trip.attractions &&
            trip.attractions.forEach((x) => {
              if (x.status.toLowerCase() === 'ok') {
                if (x.item.startTime) {
                  x.item = EventTimeService.processEventTime(x.item);
                }
                array.push(x);
              }
            });
          trip.attractions = array;
        });
      }
      return data;
    });
  }

  public getUserTripByTripID(hubName, tripID) {
    return this.apiService.get(`visitors/${hubName}/trips/${tripID}`).map((data) => {
      if (data && data.trips && data.trips.length) {
        data.trips.forEach((trip) => {
          let array = [];
          trip.attractions &&
            trip.attractions.forEach((x) => {
              if (x.status.toLowerCase() === 'ok') {
                if (x.item.startTime) {
                  x.item = EventTimeService.processEventTime(x.item);
                }
                array.push(x);
              }
            });
          trip.attractions = array;
        });
      }
      return data;
    });
  }

  public createTrip(hubName, command: CreateWholeTripCommand) {
    return this.apiService.post(`visitors/${hubName}/trips`, command);
  }

  public createTripTemp(hubName, command: CreateTripTempCommand) {
    return this.apiService.post(`visitors/${hubName}/trips/trip-temp`, command);
  }

  public getTripTemp(hubName, id: string) {
    return this.apiService.get(`visitors/${hubName}/trips/trip-temp/${id}`);
  }

  public syncTripsToDB(hubName, command: CreateWholeTripCommand) {
    return this.apiService.post(`visitors/${hubName}/trips/sync-trip-to-db`, command);
  }

  public deleteTrip(hubName, command: any) {
    return this.apiService.post(`visitors/${hubName}/trips/delete-trip`, command);
  }

  public updateTrip(hubName, command: any) {
    return this.apiService.put(`visitors/${hubName}/trips`, command);
  }

  public updateTripTitle(hubName, command: any) {
    return this.apiService.put(`visitors/${hubName}/trips/trip-title`, command);
  }

  public addAtractionToTrip(hubName, command: any) {
    return this.apiService.put(`visitors/${hubName}/trips/add-attraction`, command);
  }

  public removeAtractionFromTrip(hubName, command: any) {
    return this.apiService.put(`visitors/${hubName}/trips/remove-attraction`, command);
  }

  public getTourDetail(hubName: string, tourId, fromVisitor = false): Observable<HubTour> {
    return this.apiService.get(`hubs/${hubName}/tours/${tourId}/${fromVisitor}`).map((y) => {
      y.attractions.forEach((x) => {
        if (x.status.toLowerCase() === 'ok') {
          if (x.item.startTime) {
            x.item = EventTimeService.processEventTime(x.item);
          }
          if (!isNullOrUndefined(x.item.lat) && !isNullOrUndefined(x.item.lng)) {
            x.item.address = {
              address: x.item.address,
              lat: x.item.lat,
              lng: x.item.lng,
            };
          }
        }
      });
      return y;
    });
  }

  public setDefaultTravelMode(
    hubName: string,
    tourId: string | String,
    mode: string,
    canChange: boolean
  ): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/tours/${tourId}/default-travel-mode`, { mode, canChange });
  }

  public sendEmails(emailAddresses, tripUrl): Observable<any> {
    return this.apiService.post(`common/send-emails`, { emailAddresses, tripUrl });
  }

  public getInquiryEmail(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/inquiry-email`);
  }

  public getImageBase64(imgUrl): Observable<any> {
    return this.apiService.get(`common/image-base64?url=${imgUrl}`);
  }

  public sendHelp(help): Observable<any> {
    return this.apiService.post(`common/send-help`, help);
  }

  public getToggles(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/toggles`);
  }

  public getDataSource(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/datasource`);
  }

  public convertGoogleImages(hubName: string, items: any): Observable<any> {
    return this.apiService.post(`visitors/${hubName}/convert-google-images`, { items });
  }

  public createEvent(hubName, command: VisitorCreateEventCommand, frequencyEventTimes: any[]) {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }
    if (frequencyEventTimes && frequencyEventTimes.length) {
      formData.set('eventTimes', JSON.stringify(frequencyEventTimes));
    } else {
      formData.set('eventTimes', JSON.stringify(command.eventTimes));
    }
    formData.set('address', JSON.stringify(command.address));
    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.post(`visitors/${hubName}/events`, formData);
  }

  public createPlace(hubName, command: CreatePlaceCommand) {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }
    formData.set('address', JSON.stringify(command.address));
    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.post(`visitors/${hubName}/places`, formData);
    // return Observable.of(list);
  }

  public updatePlaceAnalytics(hubName: string, placeId: string, data: PlaceAnalytics) {
    return this.apiService.put(`visitors/${hubName}/places/analytics`, {
      placeId,
      data,
    });
  }

  public updateEventAnalytics(hubName: string, eventId: string, data: EventAnalytics) {
    return this.apiService.put(`visitors/${hubName}/events/analytics`, {
      eventId,
      data,
    });
  }

  public updateTripPlanerAnalytics(hubName: string, data: HubAnalytics) {
    return this.apiService.put(`visitors/${hubName}/tours/analytics`, {
      data,
    });
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/services/visitor.api.service.ts